/**
 * Donation Category Chart Component
 *
 * Pie chart showing donation distribution by category
 */

//import { percent } from 'motion/react';
import { FC } from 'react';
import {
  PieChart,
  Pie,
  PieLabelRenderProps,
  PieSectorShapeProps,
  Sector,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import type { DashboardChartsResponse } from '@/types/dashboard.types';

interface DonationCategoryChartProps {
  months: number;
  chartsData: DashboardChartsResponse | null;
}

const getPeriodDescription = (months: number): string => {
  if (months === 6) return 'Last 6 months';
  if (months === 12) return 'Last 12 months';
  return 'Current year';
};

export const DonationCategoryChart: FC<DonationCategoryChartProps> = ({ months, chartsData }) => {
  const COLORS = chartsData?.category_breakdown.map((item) => item.color || '#ccc') || [];
  const RADIAN = Math.PI / 180;

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }: PieLabelRenderProps) => {
    if (cx == null || cy == null || innerRadius == null || outerRadius == null) {
      return null;
    }
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const ncx = Number(cx);
    const x = ncx + radius * Math.cos(-(midAngle ?? 0) * RADIAN);
    const ncy = Number(cy);
    const y = ncy + radius * Math.sin(-(midAngle ?? 0) * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > ncx ? 'start' : 'end'}
        dominantBaseline="central"
      >
        {`${((percent ?? 1) * 100).toFixed(0)}%`}
      </text>
    );
  };

  const MyCustomPie = (props: PieSectorShapeProps) => {
    return <Sector {...props} fill={COLORS[props.index % COLORS.length]} />;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Donations by Category</h3>
        <p className="text-sm text-gray-600">{getPeriodDescription(months)}</p>
      </div>
      <ResponsiveContainer width="100%" height={400}>
        <PieChart>
          <Pie
            data={chartsData?.category_breakdown}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={140}
            fill="#8884d8"
            dataKey="amount"
            nameKey="category_name"
            shape={MyCustomPie}
          ></Pie>
          <Tooltip
            formatter={(value: number | undefined) =>
              value !== undefined
                ? `$${value.toLocaleString('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}`
                : '$0.00'
            }
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '8px 12px',
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="mt-6 space-y-2">
        {chartsData?.category_breakdown.map((item) => (
          <div key={item.category_name} className="flex items-center justify-between">
            <div className="flex items-center">
              <div
                className="w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: item.color || '#ccc' }}
              />
              <span className="text-sm text-gray-700">{item.category_name}</span>
            </div>
            <span className="text-sm font-semibold text-gray-900">
              $
              {item.amount.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
